import { useState, useCallback, useMemo, useEffect } from 'react';
import { conceptExplanations } from '../data/conceptExplanations';
import storageManager from '../utils/storageManager';
import { trackBookmarkToggle } from '../utils/analytics';

// useBookmarks Hook - Extracted from App.js for better maintainability
// Handles bookmark functionality including local storage persistence

const useBookmarks = (getDomainFromKey) => {
  const [bookmarkedConcepts, setBookmarkedConcepts] = useState(new Set());
  const [showBookmarks, setShowBookmarks] = useState(false);

  // Toggle bookmark functionality (optimized with useCallback)
  const toggleBookmark = useCallback((conceptKey) => {
    // Prevent null/undefined keys from being bookmarked
    if (!conceptKey || conceptKey === null || conceptKey === undefined) {
      console.warn('Attempted to bookmark invalid key:', conceptKey);
      return;
    }
    
    const newBookmarks = new Set(bookmarkedConcepts);
    const isBookmarked = newBookmarks.has(conceptKey);
    
    if (isBookmarked) {
      newBookmarks.delete(conceptKey);
    } else {
      newBookmarks.add(conceptKey);
    }
    setBookmarkedConcepts(newBookmarks);
    
    // Track bookmark toggle in Google Analytics
    trackBookmarkToggle(conceptKey, !isBookmarked);
    
    // Persist to localStorage using storage manager
    storageManager.bookmarks.save(newBookmarks);
  }, [bookmarkedConcepts]);

  // Load bookmarks from localStorage using storage manager
  const loadBookmarks = useCallback(() => {
    const bookmarksSet = storageManager.bookmarks.load();
    
    // Clean up any null/undefined values from existing bookmarks
    const cleanedBookmarks = new Set();
    bookmarksSet.forEach(key => {
      if (key && key !== null && key !== undefined) {
        cleanedBookmarks.add(key);
      }
    });
    
    // If we cleaned up any invalid keys, save the cleaned version
    if (cleanedBookmarks.size !== bookmarksSet.size) {
      console.log('Cleaned up invalid bookmark keys. Before:', bookmarksSet.size, 'After:', cleanedBookmarks.size);
      storageManager.bookmarks.save(cleanedBookmarks);
    }
    
    setBookmarkedConcepts(cleanedBookmarks);
  }, []);

  // Memoized function to get bookmarked concepts (after conceptExplanations is defined)
  const getBookmarkedConcepts = useMemo(() => {
    try {
      const result = [];
      const missingKeys = [];
      
      if (!bookmarkedConcepts || bookmarkedConcepts.size === 0) {
        return [];
      }
      
      bookmarkedConcepts.forEach(key => {
        if (!key || key === null || key === undefined) {
          missingKeys.push(key);
          return;
        }
        
        const concept = conceptExplanations[key];
        if (concept) {
          result.push({
            key,
            ...concept,
            domain: getDomainFromKey(key)
          });
        } else {
          missingKeys.push(key);
        }
      });
      
      // Debug: Log any bookmarked keys that don't exist in conceptExplanations
      if (missingKeys.length > 0) {
        console.warn('Bookmarked concepts not found in conceptExplanations:', missingKeys);
        console.warn('Total bookmarked concepts:', bookmarkedConcepts.size);
        console.warn('Found concepts:', result.length);
      }
      
      return result.sort((a, b) => (a.title || '').localeCompare(b.title || ''));
    } catch (error) {
      console.error('Error in getBookmarkedConcepts:', error);
      return [];
    }
  }, [bookmarkedConcepts, getDomainFromKey]);

  // Load bookmarks on mount
  useEffect(() => {
    loadBookmarks();
  }, [loadBookmarks]);

  return {
    bookmarkedConcepts,
    showBookmarks,
    setShowBookmarks,
    toggleBookmark,
    getBookmarkedConcepts,
    loadBookmarks
  };
};

export default useBookmarks;